import { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { trackEvent } from './analytics';
import scratcherBg from './assets/scratcher-bg.jpg';

/**
 * ScratcherOverlay — the modern scratch-off canvas mechanism.
 *
 * Ported to JS/React 17 from the vendored scratcher-package (ScratcherOverlay.tsx).
 * Logic is kept verbatim (DPR-aware dual canvas, particle physics, Web Audio
 * scratch sound, shimmer, 33% reveal threshold, auto-scratch finish, ResizeObserver
 * self-healing paint). The only behavioural change vs. the source is that the
 * overlay image is loaded with `crossOrigin="anonymous"` so the microsite's
 * cross-origin CDN overlay artwork doesn't taint the canvas — without it the
 * `getImageData` reveal-detection read throws a SecurityError on remote images.
 *
 * Props:
 *   - onComplete: () => void   fired once the scratch reveal finishes (after fade)
 *   - imageUrl?: string        overlay artwork; falls back to the bundled asset
 */

// Fallback flake colors (silver scratch-latex tones) — used only when the
// artwork sampler isn't available. Normally every flake samples its color
// from the exact pixel of the overlay artwork being scratched away.
const FALLBACK_FLAKE_COLORS = ['#C9C9C9', '#ADADAD', '#E8E8E8', '#969696'];

const ScratcherOverlay = ({ onComplete, imageUrl }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const particleCanvasRef = useRef(null);
  const [isScratching, setIsScratching] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [showRevealButton, setShowRevealButton] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [shimmerKey, setShimmerKey] = useState(0);
  const [showShimmer, setShowShimmer] = useState(false);
  const lastPosRef = useRef(null);
  const imageLoadedRef = useRef(false);
  const particlesRef = useRef([]);
  // Small offscreen copy of the painted artwork ({data, sw, sh}) used to color
  // flakes from the pixels being scratched away — rebuilt on every paint().
  const artSamplerRef = useRef(null);
  const animationFrameRef = useRef(0);
  const autoScratchRef = useRef(0);
  const audioContextRef = useRef(null);
  const scratchSoundRef = useRef(null);
  const gainNodeRef = useRef(null);
  // Completion latch: handleComplete can be triggered by BOTH the scratch
  // threshold and the Reveal button (or by further scratching during the
  // fade-out, since the canvas stays mounted until onComplete unmounts us).
  // Without it, each re-trigger re-fires analytics and queues another
  // onComplete timeout.
  const completedRef = useRef(false);

  const REVEAL_THRESHOLD = 33;
  // Brush radius scales with card width. Cap keeps it sane on wide viewports.
  const BRUSH_RADIUS_RATIO = 0.14;
  const BRUSH_RADIUS_MAX = 56;

  // Show reveal button after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowRevealButton(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Shimmer effect - starts after card fly-in animation (0.6s), then loops every 5 seconds
  useEffect(() => {
    // Wait for card fly-in animation to complete (0.6s + small buffer)
    const initialDelay = setTimeout(() => {
      setShowShimmer(true);
      setShimmerKey(1);
    }, 800);

    // Set up 5 second loop for shimmer
    const interval = setInterval(() => {
      if (!hasStarted && !isFadingOut) {
        setShimmerKey(prev => prev + 1);
      }
    }, 5000);

    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, [hasStarted, isFadingOut]);

  // Initialize audio context for scratch sound
  const initAudio = useCallback(() => {
    if (audioContextRef.current) return;

    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.connect(audioContextRef.current.destination);
      gainNodeRef.current.gain.value = 0;
    } catch (e) {
      // Audio unsupported (old browser / iframe restrictions) — scratch works silently.
    }
  }, []);

  // Start scratch sound
  const startScratchSound = useCallback(() => {
    if (!audioContextRef.current || !gainNodeRef.current) {
      initAudio();
    }

    if (!audioContextRef.current || !gainNodeRef.current || scratchSoundRef.current) return;

    // Modern browsers (esp. iOS Safari, Chrome) suspend new AudioContexts
    // until a user gesture explicitly resumes them. We're inside a pointer-
    // down handler so the resume is allowed; without it the noise plays
    // into a silent context and the user hears nothing.
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => { /* noop */ });
    }

    try {
      // Create white noise for scratching sound
      const bufferSize = 2 * audioContextRef.current.sampleRate;
      const noiseBuffer = audioContextRef.current.createBuffer(1, bufferSize, audioContextRef.current.sampleRate);
      const output = noiseBuffer.getChannelData(0);

      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1;
      }

      const whiteNoise = audioContextRef.current.createBufferSource();
      whiteNoise.buffer = noiseBuffer;
      whiteNoise.loop = true;

      // Filter to make it sound more like scratching
      const filter = audioContextRef.current.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 800;
      filter.Q.value = 0.5;

      whiteNoise.connect(filter);
      filter.connect(gainNodeRef.current);

      // Fade in to a clearly-audible level.
      gainNodeRef.current.gain.setTargetAtTime(0.25, audioContextRef.current.currentTime, 0.02);

      whiteNoise.start();
      scratchSoundRef.current = whiteNoise;
    } catch (e) {
      // Sound is a flourish — never let an audio failure break scratching.
    }
  }, [initAudio]);

  // Stop scratch sound
  const stopScratchSound = useCallback(() => {
    if (scratchSoundRef.current && gainNodeRef.current && audioContextRef.current) {
      try {
        gainNodeRef.current.gain.setTargetAtTime(0, audioContextRef.current.currentTime, 0.05);
        setTimeout(() => {
          if (scratchSoundRef.current) {
            scratchSoundRef.current.stop?.();
            scratchSoundRef.current = null;
          }
        }, 100);
      } catch (e) {
        scratchSoundRef.current = null;
      }
    }
  }, []);

  const getBrushRadius = useCallback(() => {
    const container = containerRef.current;
    if (!container) return BRUSH_RADIUS_MAX;
    const width = container.getBoundingClientRect().width;
    return Math.min(width * BRUSH_RADIUS_RATIO, BRUSH_RADIUS_MAX);
  }, []);

  // Color of the artwork at a container-local point, with brightness jitter so
  // flakes from one spot don't look uniform (suggests material thickness).
  // Falls back to silver latex tones when the sampler isn't available.
  const sampleFlakeColor = useCallback((localX, localY, rectW, rectH) => {
    const s = artSamplerRef.current;
    if (!s || !rectW || !rectH) {
      return FALLBACK_FLAKE_COLORS[Math.floor(Math.random() * FALLBACK_FLAKE_COLORS.length)];
    }
    const sx = Math.min(s.sw - 1, Math.max(0, Math.round((localX / rectW) * s.sw)));
    const sy = Math.min(s.sh - 1, Math.max(0, Math.round((localY / rectH) * s.sh)));
    const i = (sy * s.sw + sx) * 4;
    const bright = 0.8 + Math.random() * 0.4; // ±20% depth variation
    const r = Math.min(255, Math.round(s.data[i] * bright));
    const g = Math.min(255, Math.round(s.data[i + 1] * bright));
    const b = Math.min(255, Math.round(s.data[i + 2] * bright));
    return `rgb(${r},${g},${b})`;
  }, []);

  const calculatePercentCleared = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;

    const ctx = canvas.getContext('2d');
    if (!ctx) return 0;

    let imageData;
    try {
      imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    } catch (e) {
      // Tainted canvas — only happens on the last-resort non-CORS image load.
      // Pixel-based progress is unavailable; the "Reveal Offer" button (which
      // auto-scratches without reading pixels) becomes the reveal path.
      return 0;
    }
    const pixels = imageData.data;
    let transparent = 0;

    // Sample every 10th pixel for performance
    for (let i = 3; i < pixels.length; i += 40) {
      if (pixels[i] === 0) transparent++;
    }

    // Guard against a 0×0 canvas (no samples) → avoid 0/0 = NaN, which would
    // make `cleared >= REVEAL_THRESHOLD` silently never fire.
    const samples = pixels.length / 40;
    return samples > 0 ? (transparent / samples) * 100 : 0;
  }, []);

  // Particle animation loop
  const animateParticles = useCallback(() => {
    const canvas = particleCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const dpr = window.devicePixelRatio || 1;

    particlesRef.current = particlesRef.current.filter(p => {
      // Physics: gravity, air drag, and a falling-leaf flutter.
      p.vy += 0.28; // gravity
      p.vx *= 0.985; // air drag
      p.phase += 0.12;
      p.x += p.vx + Math.sin(p.phase) * p.flutter * 0.6;
      p.y += p.vy;
      p.rotation += p.rotationSpeed + Math.sin(p.phase) * 0.02;
      p.opacity -= 0.008;
      p.size *= 0.997; // flakes crumble slightly as they fall

      // Remove if off screen, faded, or crumbled away
      if (p.y > canvas.height / dpr + 50 || p.opacity <= 0 || p.size < 0.4) return false;

      // Draw particle (small irregular shapes like scratch shavings)
      ctx.save();
      ctx.translate(p.x * dpr, p.y * dpr);
      ctx.rotate(p.rotation);
      ctx.globalAlpha = p.opacity;
      ctx.fillStyle = p.color;

      // Draw irregular shaving shape
      ctx.beginPath();
      const size = p.size * dpr;
      ctx.moveTo(-size, -size * 0.3);
      ctx.quadraticCurveTo(-size * 0.5, -size, size * 0.3, -size * 0.5);
      ctx.quadraticCurveTo(size, -size * 0.2, size, size * 0.3);
      ctx.quadraticCurveTo(size * 0.5, size, -size * 0.3, size * 0.5);
      ctx.quadraticCurveTo(-size, size * 0.2, -size, -size * 0.3);
      ctx.fill();
      // Subtle darker edge so flakes read as curled chips, not flat dots.
      ctx.lineWidth = Math.max(0.5, size * 0.12);
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.18)';
      ctx.stroke();
      ctx.restore();

      return true;
    });

    if (particlesRef.current.length > 0 || isScratching) {
      animationFrameRef.current = requestAnimationFrame(animateParticles);
    }
  }, [isScratching]);

  // Spawn flakes at the scratch position. `strokeVX/strokeVY` (css px per
  // event) bias the flake velocity along the scratch direction so shavings
  // fling off the stroke instead of just dropping straight down.
  const spawnParticles = useCallback((x, y, strokeVX = 0, strokeVY = 0) => {
    // Respect reduced-motion: the scratch interaction still works, we just
    // skip the decorative flake shower. Checked per spawn so OS toggles apply.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const localX = x - rect.left;
    const localY = y - rect.top;

    // 3-6 flakes per movement; faster strokes shed a few more.
    const speed = Math.min(40, Math.hypot(strokeVX, strokeVY));
    const count = Math.floor(Math.random() * 4) + 3 + Math.round(speed / 14);

    for (let i = 0; i < count; i++) {
      const jx = localX + (Math.random() - 0.5) * 20;
      const jy = localY + (Math.random() - 0.5) * 20;
      const particle = {
        x: jx,
        y: jy,
        vx: strokeVX * 0.12 + (Math.random() - 0.5) * 2.2,
        vy: Math.max(-2, strokeVY * 0.12) + Math.random() * 2 + 0.8,
        size: Math.random() * 4 + 2,
        color: sampleFlakeColor(jx, jy, rect.width, rect.height),
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.25,
        opacity: 0.85 + Math.random() * 0.15,
        phase: Math.random() * Math.PI * 2,
        flutter: 0.4 + Math.random() * 0.9,
      };
      particlesRef.current.push(particle);
    }

    // Cap the pool so long scratch sessions can't grow unbounded.
    const MAX_FLAKES = 240;
    if (particlesRef.current.length > MAX_FLAKES) {
      particlesRef.current.splice(0, particlesRef.current.length - MAX_FLAKES);
    }

    // Start animation if not running
    if (!animationFrameRef.current) {
      animateParticles();
    }
  }, [animateParticles, sampleFlakeColor]);

  // Shared completion tail: fade the (whatever remains of the) coating out
  // and hand control back to the host.
  const finishReveal = useCallback(() => {
    setIsFadingOut(true);
    setTimeout(() => onComplete(), 500);
  }, [onComplete]);

  // Celebratory rain of shavings — colored from random points of the artwork.
  const spawnFinalBurst = useCallback(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    for (let i = 0; i < 30; i++) {
      const px = Math.random() * rect.width;
      const py = Math.random() * rect.height * 0.6;
      const particle = {
        x: px,
        y: -20 - Math.random() * 100,
        vx: (Math.random() - 0.5) * 4,
        vy: Math.random() * 3 + 2,
        size: Math.random() * 6 + 3,
        color: sampleFlakeColor(px, py, rect.width, rect.height),
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.3,
        opacity: 1,
        phase: Math.random() * Math.PI * 2,
        flutter: 0.4 + Math.random() * 0.9,
      };
      particlesRef.current.push(particle);
    }
    animateParticles();
  }, [animateParticles, sampleFlakeColor]);

  // Manual-scratch completion (user cleared the threshold themselves): just
  // erase what's left in one smooth fade — replaying a scripted swipe over a
  // card they already scratched reads as jarring.
  const handleComplete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    trackEvent('scratcher_completed');
    stopScratchSound();
    spawnFinalBurst();
    finishReveal();
  }, [stopScratchSound, spawnFinalBurst, finishReveal]);

  // Scripted reveal (the "Reveal Offer" button): swipe-scratch the card with
  // diagonal strokes — the original package animation — then clear and fade.
  const autoScratch = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) { finishReveal(); return; }
    const ctx = canvas.getContext('2d');
    if (!ctx) { finishReveal(); return; }

    const rect = container.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const brushRadius = getBrushRadius() * 2;

    // Diagonal swipe paths across the card.
    const swipes = [
      { startX: 0, startY: 0, endX: rect.width, endY: rect.height * 0.6 },
      { startX: rect.width, startY: 0, endX: 0, endY: rect.height * 0.7 },
      { startX: 0, startY: rect.height * 0.3, endX: rect.width, endY: rect.height },
      { startX: rect.width * 0.5, startY: 0, endX: rect.width * 0.5, endY: rect.height },
    ];

    let currentSwipe = 0;
    let swipeProgress = 0;
    const stepsPerSwipe = 15;

    const animate = () => {
      if (currentSwipe >= swipes.length) {
        // Clear whatever the swipes missed, then fade out the (now empty) layer.
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        finishReveal();
        return;
      }

      const swipe = swipes[currentSwipe];
      swipeProgress++;

      const t = swipeProgress / stepsPerSwipe;
      const x = swipe.startX + (swipe.endX - swipe.startX) * t;
      const y = swipe.startY + (swipe.endY - swipe.startY) * t;

      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(x * scaleX, y * scaleY, brushRadius * scaleX, 0, Math.PI * 2);
      ctx.fill();

      // Connect to the previous point so the stroke is continuous.
      if (swipeProgress > 1) {
        const prevT = (swipeProgress - 1) / stepsPerSwipe;
        const prevX = swipe.startX + (swipe.endX - swipe.startX) * prevT;
        const prevY = swipe.startY + (swipe.endY - swipe.startY) * prevT;
        ctx.beginPath();
        ctx.lineWidth = brushRadius * 2 * scaleX;
        ctx.lineCap = 'round';
        ctx.moveTo(prevX * scaleX, prevY * scaleY);
        ctx.lineTo(x * scaleX, y * scaleY);
        ctx.stroke();
      }

      // Flakes fling along the swipe direction (client coords + stroke vector).
      const stepVX = (swipe.endX - swipe.startX) / stepsPerSwipe;
      const stepVY = (swipe.endY - swipe.startY) / stepsPerSwipe;
      spawnParticles(rect.left + x, rect.top + y, stepVX, stepVY);

      if (swipeProgress >= stepsPerSwipe) {
        currentSwipe++;
        swipeProgress = 0;
      }

      autoScratchRef.current = requestAnimationFrame(animate);
    };

    autoScratchRef.current = requestAnimationFrame(animate);
  }, [getBrushRadius, spawnParticles, finishReveal]);

  const scratch = useCallback((x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const brushRadius = getBrushRadius();
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const canvasX = (x - rect.left) * scaleX;
    const canvasY = (y - rect.top) * scaleY;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(canvasX, canvasY, brushRadius * scaleX, 0, Math.PI * 2);
    ctx.fill();

    // Draw line from last position for smooth scratching
    if (lastPosRef.current) {
      ctx.beginPath();
      ctx.lineWidth = brushRadius * 2 * scaleX;
      ctx.lineCap = 'round';
      ctx.moveTo(lastPosRef.current.x * scaleX, lastPosRef.current.y * scaleY);
      ctx.lineTo(canvasX, canvasY);
      ctx.stroke();
    }

    // Stroke direction (css px per event) — flakes fling along the scratch.
    const prev = lastPosRef.current;
    const newPos = { x: canvasX / scaleX, y: canvasY / scaleY };
    const strokeVX = prev ? newPos.x - prev.x : 0;
    const strokeVY = prev ? newPos.y - prev.y : 0;
    lastPosRef.current = newPos;

    // Spawn shaving particles
    spawnParticles(x, y, strokeVX, strokeVY);
  }, [getBrushRadius, spawnParticles]);

  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    // Capture the pointer so the stroke keeps tracking even when it drifts
    // off the canvas edge — without it, pointerleave ends the stroke
    // mid-scratch and the user has to press again.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
    setIsScratching(true);
    startScratchSound();
    if (!hasStarted) {
      setHasStarted(true);
      trackEvent('scratcher_started');
    }
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      lastPosRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      scratch(e.clientX, e.clientY);
    }
  }, [hasStarted, scratch, startScratchSound]);

  const handlePointerMove = useCallback((e) => {
    if (!isScratching) return;
    e.preventDefault();
    scratch(e.clientX, e.clientY);
  }, [isScratching, scratch]);

  const handlePointerUp = useCallback(() => {
    setIsScratching(false);
    stopScratchSound();
    lastPosRef.current = null;

    const cleared = calculatePercentCleared();

    if (cleared >= REVEAL_THRESHOLD) {
      handleComplete();
    }
  }, [calculatePercentCleared, handleComplete, stopScratchSound]);

  // Button reveal: play the scripted swipe-scratch across the whole card
  // (the user hasn't scratched, so the animation IS the reveal moment).
  const handleRevealClick = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    trackEvent('scratcher_reveal_button_clicked');
    trackEvent('scratcher_completed');
    stopScratchSound();
    spawnFinalBurst();
    autoScratch();
  }, [stopScratchSound, spawnFinalBurst, autoScratch]);

  // Initialize canvas with image background
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const particleCanvas = particleCanvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !particleCanvas) return;

    const img = new Image();
    // The microsite's overlay artwork is served from a cross-origin CDN. Request
    // it with CORS so drawing it to the canvas doesn't taint it — otherwise the
    // getImageData() read in calculatePercentCleared() throws a SecurityError and
    // the reveal threshold can never be measured. (The bundled local asset is
    // same-origin and unaffected.)
    img.crossOrigin = 'anonymous';
    let imageReady = false;
    let objectUrl = null;
    const imgSrc = imageUrl || scratcherBg;
    // Remote artwork (cross-origin CDN) vs the bundled same-origin fallback.
    const isRemote = /^https?:\/\//i.test(imgSrc) && imgSrc.indexOf(window.location.origin) !== 0;

    // Self-healing paint: every call rewrites canvas backing AND its CSS
    // pixel dimensions atomically (no conditional guards), then redraws
    // with `object-fit: cover` math. An inline `aspect-ratio` is also
    // pinned with !important so backing/display aspect can never drift.
    const paint = () => {
      // Once the reveal has started (threshold hit, button pressed, or fade
      // running) the coating must never come back — a resize/visibility
      // repaint here would redraw it over the auto-scratch swipes or the
      // user's cleared card.
      if (completedRef.current) return;
      const dpr = window.devicePixelRatio || 1;
      const w = container.offsetWidth;
      const h = container.offsetHeight;
      if (w === 0 || h === 0) return;

      const backingW = Math.round(w * dpr);
      const backingH = Math.round(h * dpr);

      // Always sync backing — assigning to canvas.width/height clears the
      // canvas state, which is fine because we redraw the image below.
      canvas.width = backingW;
      canvas.height = backingH;
      particleCanvas.width = backingW;
      particleCanvas.height = backingH;

      // Pin DISPLAY size to FILL the host (100%, not fixed px) with !important.
      // 100% (rather than the measured px) means the canvas keeps covering the
      // offer the instant the host grows — e.g. when the async content-terms
      // fetch resolves and the terms block lengthens the card, or fonts reflow —
      // BEFORE ResizeObserver repaints the backing. Otherwise a px-pinned canvas
      // stays frozen at the old height and the bottom of the offer is briefly
      // exposed (the visible "loads, then loads again" flash). The backing
      // (canvas.width/height above) still drives crisp resolution; a momentary
      // bitmap stretch before repaint is invisible vs. leaking the prize. The
      // !important still blocks host CSS (Tailwind preflight, partner styles)
      // from shrinking it.
      canvas.style.setProperty('width', '100%', 'important');
      canvas.style.setProperty('height', '100%', 'important');
      particleCanvas.style.setProperty('width', '100%', 'important');
      particleCanvas.style.setProperty('height', '100%', 'important');

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // Reset to identity, clear in backing coords, then scope the dpr
      // scale to JUST the image draw with save/restore. The context is
      // left in identity mode so scratch()/autoScratch() can work in
      // backing-pixel coords (matching the legacy math).
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, backingW, backingH);
      ctx.save();
      ctx.scale(dpr, dpr);

      if (imageReady && img.naturalWidth && img.naturalHeight) {
        // `object-fit: cover` — fill the container, crop the dominant axis.
        const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
        const drawW = img.naturalWidth * scale;
        const drawH = img.naturalHeight * scale;
        const dx = (w - drawW) / 2;
        const dy = (h - drawH) / 2;
        ctx.drawImage(img, dx, dy, drawW, drawH);
      } else {
        // Neutral slate fallback (only ever visible if the artwork genuinely
        // fails to load) — far less jarring than the old purple if it flashes.
        const gradient = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w);
        gradient.addColorStop(0, '#3A3F4A');
        gradient.addColorStop(0.4, '#2A2E37');
        gradient.addColorStop(1, '#1C1F26');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, w, h);
      }
      ctx.restore(); // back to identity — scratch() works in backing coords

      // Rebuild the flake-color sampler: a small offscreen copy of the freshly
      // painted (unscratched) artwork. One tiny readback per paint; scratching
      // then samples colors via pure array lookups, no canvas reads.
      try {
        const sw = 128;
        const sh = Math.max(1, Math.round((h / w) * sw));
        const sc = document.createElement('canvas');
        sc.width = sw;
        sc.height = sh;
        const sctx = sc.getContext('2d', { willReadFrequently: true });
        sctx.drawImage(canvas, 0, 0, sw, sh);
        artSamplerRef.current = { data: sctx.getImageData(0, 0, sw, sh).data, sw, sh };
      } catch (err) {
        // Tainted canvas or other read failure → neutral fallback flakes.
        artSamplerRef.current = null;
      }
    };

    // Robust load ladder. The common path (attempt 1) is unchanged — a
    // crossOrigin <img>, which benefits from the ContentDetails preload and
    // keeps the canvas untainted so getImageData() reveal-detection works. If it
    // fails — a poisoned non-CORS HTTP cache entry left by an older build, or a
    // privacy extension / shield that strips CORS response headers — escalate so
    // the artwork still appears instead of the dark fallback:
    //   2) fetch → blob → object URL : a fresh refetch (cache:'reload') bypasses
    //      the poisoned cache, and the blob is SAME-ORIGIN so the canvas is not
    //      tainted → getImageData() still works (manual scratch unaffected).
    //   3) plain <img>, cache-busted : last resort. May taint the canvas (remote,
    //      no CORS) — the art shows and reveal falls back to the "Reveal Offer"
    //      button (which auto-scratches without reading pixels).
    let attempt = 0;
    const loadAttempt = () => {
      attempt += 1;
      if (attempt === 2 && isRemote) {
        fetch(imgSrc, { mode: 'cors', cache: 'reload', credentials: 'omit' })
          .then((r) => { if (!r.ok) throw new Error('http ' + r.status); return r.blob(); })
          .then((blob) => {
            objectUrl = URL.createObjectURL(blob);
            img.removeAttribute('crossorigin');
            img.src = objectUrl;
          })
          .catch(() => loadAttempt());
      } else if (attempt >= 3 && isRemote) {
        img.removeAttribute('crossorigin');
        img.src = imgSrc + (imgSrc.indexOf('?') > -1 ? '&' : '?') + '_a8r=' + Date.now();
      } else {
        img.crossOrigin = 'anonymous';
        img.src = imgSrc;
      }
    };

    img.onload = () => {
      imageReady = true;
      imageLoadedRef.current = true;
      paint();
    };
    img.onerror = () => {
      if (isRemote && attempt < 3) { loadAttempt(); return; }
      imageReady = false;
      paint();
    };
    loadAttempt();
    // If the artwork was preloaded (ContentDetails warms it with the SAME
    // crossOrigin mode), attempt 1 is already complete here — mark it ready
    // synchronously so the very first paint() draws the artwork and no fallback
    // flashes.
    if (img.complete && img.naturalWidth) {
      imageReady = true;
      imageLoadedRef.current = true;
    }

    // Paint immediately, then again after the next two animation frames so
    // any layout settling after fly-in / vh resolution / font load is caught.
    paint();
    const raf1 = requestAnimationFrame(() => {
      paint();
      requestAnimationFrame(paint);
    });
    const t1 = window.setTimeout(paint, 100);
    const t2 = window.setTimeout(paint, 500);

    const ro = new ResizeObserver(() => paint());
    ro.observe(container);
    // Also observe the card itself in case the overlay's layout depends on it.
    const card = container.parentElement;
    if (card) ro.observe(card);

    const handleResize = () => paint();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    window.addEventListener('load', handleResize);

    const onAnimEnd = () => paint();
    card?.addEventListener('animationend', onAnimEnd);
    card?.addEventListener('transitionend', onAnimEnd);

    const onVisibility = () => { if (!document.hidden) paint(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onVisibility);

    return () => {
      cancelAnimationFrame(raf1);
      clearTimeout(t1);
      clearTimeout(t2);
      ro.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      window.removeEventListener('load', handleResize);
      card?.removeEventListener('animationend', onAnimEnd);
      card?.removeEventListener('transitionend', onAnimEnd);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onVisibility);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (autoScratchRef.current) {
        cancelAnimationFrame(autoScratchRef.current);
      }
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      stopScratchSound();
    };
  }, [imageUrl, stopScratchSound]);

  return (
    <div
      ref={containerRef}
      className={`av8s-overlay ${isFadingOut ? 'av8s-overlay--hidden' : ''}`}
    >
      <canvas
        ref={canvasRef}
        className="av8s-scratch-canvas"
        role="img"
        aria-label="Scratch-off cover — scratch to reveal your offer, or use the Reveal Offer button"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />

      {/* Particle canvas for falling shavings */}
      <canvas
        ref={particleCanvasRef}
        className="av8s-particle-canvas"
      />

      {/* Shimmer effect overlay */}
      {showShimmer && !hasStarted && !isFadingOut && (
        <div key={shimmerKey} className="av8s-shimmer">
          <div className="av8s-shimmer-sweep" />
        </div>
      )}

      {/* Accessibility fallback button — appears after 5s, but only until the
          user starts scratching; once they've engaged, it fades back out. */}
      <button
        onClick={handleRevealClick}
        className={`av8s-reveal-btn ${showRevealButton && !hasStarted ? 'av8s-reveal-btn--visible' : ''}`}
        aria-label="Reveal offer without scratching"
      >
        Reveal Offer
      </button>
    </div>
  );
};

export default ScratcherOverlay;
